import { useQuery } from '@apollo/client'
import { EmploymentAnswer } from 'candidate/components/EmploymentQuestion'
import { CurrentIntakeQuery } from 'candidate/graphql/candidateIntakes.gql'
import { CandidateIntake } from 'candidate/models/intake'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { commonRouteConfig } from 'shared/routeConfig'
import { deleteToken } from 'shared/utils/auth'
import ActiveQuestion from './ActiveQuestions'

function normalizeDate(date: string | null | undefined): string {
    if (!date) return ''
    return date
}

function buildGroupedPreferenceAnswer(
    preferences: any[],
    internalName: string,
    getChoice: (pref: any) => string | undefined
): Record<string, any> | undefined {
    if (!preferences?.length) return undefined
    const grouped: Record<string, { choice: string; customValue?: string }[]> =
        {}
    for (const pref of preferences) {
        const choice = getChoice(pref)
        if (!choice) continue
        const key = `${internalName}_${pref.order - 1}`
        if (!grouped[key]) grouped[key] = []
        const entry: { choice: string; customValue?: string } = { choice }
        if (pref.customValue) entry.customValue = pref.customValue
        grouped[key].push(entry)
    }
    return { [internalName]: grouped }
}

const Questions: React.FC = () => {
    usePageTitle('Intake')
    const toast = useToast()
    const navigate = useNavigate()
    const { candidateIntakeId } = useParams<{
        candidateIntakeId: ''
    }>()

    const [currentIntake, setCurrentIntake] = useState<
        Partial<CandidateIntake>
    >({} as CandidateIntake)
    const [currentWorkExperience, setCurrentWorkExperience] =
        useState<any>(null)
    const [candidateData, setCandidateData] = useState<any>(null)
    const { loading } = useQuery(CurrentIntakeQuery, {
        variables: { candidateIntakeId },
        onCompleted: (data) => {
            setCurrentIntake(data.currentIntake)
            setCurrentWorkExperience(
                data.currentIntake?.candidate?.currentWorkExperience ?? null
            )
            setCandidateData(data.currentIntake?.candidate ?? null)

            if (!data.currentIntake) {
                console.log('invalid candidate intake')
                // TODO: more detailed error message?
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
                deleteToken()
                return navigate(commonRouteConfig.login.path, { replace: true })
            }
        },
    })

    const existingEmploymentAnswer: EmploymentAnswer | undefined =
        currentWorkExperience
            ? {
                  company: currentWorkExperience.company
                      ? {
                            choice: currentWorkExperience.company.value,
                            id: currentWorkExperience.company.id,
                        }
                      : undefined,
                  companyOffice: currentWorkExperience.companyOffice
                      ?.officeLocation
                      ? {
                            choice: currentWorkExperience.companyOffice
                                .officeLocation.value,
                        }
                      : undefined,
                  workTitle: currentWorkExperience.title?.value ?? '',
                  startDate: normalizeDate(currentWorkExperience.startDate),
                  endDate: '',
                  isCurrent: true,
                  industries: currentWorkExperience.industries?.map(
                      (i: any) => ({
                          choice: i.industry.value,
                      })
                  ),
                  sectors: currentWorkExperience.sectors?.map((s: any) => ({
                      choice: s.sector.value,
                  })),
                  workEmail: currentWorkExperience.workEmail ?? '',
                  workPhone: currentWorkExperience.workPhone ?? '',
              }
            : undefined

    const existingPreferenceAnswers: Record<string, any> = (() => {
        if (!candidateData) return {}
        const answers: Record<string, any> = {}

        const industryAnswer = buildGroupedPreferenceAnswer(
            candidateData.industryPreferences,
            'PREF-industries',
            (p: any) => p.preferenceIndustry?.value
        )
        if (industryAnswer) answers['PREF-industries'] = industryAnswer

        const sectorAnswer = buildGroupedPreferenceAnswer(
            candidateData.sectorPreferences,
            'PREF-sectors',
            (p: any) => p.preferenceSector?.value
        )
        if (sectorAnswer) answers['PREF-sectors'] = sectorAnswer

        const locationAnswer = buildGroupedPreferenceAnswer(
            candidateData.locationPreferences,
            'PREF-locations',
            (p: any) => p.preferenceLocation?.value
        )
        if (locationAnswer) answers['PREF-locations'] = locationAnswer

        const companyAnswer = buildGroupedPreferenceAnswer(
            candidateData.companyPreferences,
            'PREF-companies',
            (p: any) => p.preferenceCompany?.value
        )
        if (companyAnswer) answers['PREF-companies'] = companyAnswer

        if (candidateData.startDate?.length) {
            answers['PREF-start-date'] = {
                'PREF-start-date': {
                    'PREF-start-date_0': candidateData.startDate.map(
                        (sd: any) => ({ choice: sd.value })
                    ),
                },
            }
        }

        return answers
    })()

    if (loading) return <LoadingPanel />

    return (
        <ActiveQuestion
            candidateIntake={currentIntake}
            existingEmploymentAnswer={existingEmploymentAnswer}
            existingWorkExperienceId={currentWorkExperience?.id}
            existingPreferenceAnswers={existingPreferenceAnswers}
        />
    )
}

export default Questions
